import { FC, ReactNode, useEffect } from "react";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { useLocation } from "react-router-dom";
import { posthogApiKey, posthogHost } from "@/config/posthog";
import { appEnv } from "@/config";
import { initWebVitals } from "@/analytics/web-vitals";

/**
 * Initialise the PostHog JS SDK.  Call once at app startup (e.g. main.tsx).
 * No-ops when the API key is empty so local dev is analytics-free by default.
 */
export function initPostHog() {
  if (!posthogApiKey) return;

  posthog.init(posthogApiKey, {
    api_host: posthogHost,
    autocapture: true,
    capture_pageview: false, // we fire manually on route change
    disable_session_recording: false,
    session_idle_timeout_seconds: 180, // 3 minutes
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: "*",
      maskTextFn: (text) => text.replace(/\d/g, "*"),
    },
  });

  posthog.register({ environment: appEnv });

  // Expose for debugging & E2E tests (posthog-js ES module doesn't set window.posthog)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).posthog = posthog;

  window.addEventListener("error", (event) => {
    posthog.capture("$exception", {
      $exception_message: event.message,
      $exception_type: "UncaughtError",
      $exception_source: event.filename,
      $exception_lineno: event.lineno,
      $exception_colno: event.colno,
      $exception_stack_trace_raw: event.error?.stack,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const error =
      event.reason instanceof Error
        ? event.reason
        : new Error(String(event.reason));
    posthog.capture("$exception", {
      $exception_message: error.message,
      $exception_type: "UnhandledRejection",
      $exception_stack_trace_raw: error.stack,
    });
  });

  initWebVitals();
}

/** Fires a `$pageview` event on every React-Router location change. */
const PostHogPageviewTracker: FC = () => {
  const location = useLocation();

  useEffect(() => {
    if (posthogApiKey) {
      posthog.capture("$pageview");
    }
  }, [location.pathname, location.search]);

  return null;
};

/** Wrap your app tree (inside BrowserRouter) with this provider. */
export const PostHogAppProvider: FC<{ children: ReactNode }> = ({ children }) => {
  if (!posthogApiKey) return <>{children}</>;

  return (
    <PostHogProvider client={posthog}>
      <PostHogPageviewTracker />
      {children}
    </PostHogProvider>
  );
};
